import type {
  Country,
  CountryDetails,
  State,
  StateWithCountry,
  StateDetails,
  City,
  CityBasic,
  LocationSelection,
  ValidationResult,
  ValidationError,
  CacheTTL
} from '../types/location';
import { LocationValidationError } from '../types/location';
import type { CountryStateCityClient } from './api/CountryStateCityClient';
import type { LocationCache } from './cache/LocationCache';
import { CacheKeys, RecommendedTTL } from './cache/LocationCache';

/**
 * Configuration for LocationService
 */
export interface LocationServiceConfig {
  apiClient: CountryStateCityClient;
  cacheManager: LocationCache;
  ttl: CacheTTL;
}

/**
 * High-level service that coordinates APIClient and CacheManager
 * 
 * Features:
 * - Cache-first data fetching
 * - Automatic cache population
 * - Hierarchical location validation
 * - Request deduplication
 * - Fallback to expired cache on API failure
 * - User-friendly error messages
 */
export class LocationService {
  private readonly apiClient: CountryStateCityClient;
  private readonly cacheManager: LocationCache;
  private readonly ttl: CacheTTL;
  private readonly pendingRequests: Map<string, Promise<unknown>>;
  private lastSync: Date | null;

  constructor(config: LocationServiceConfig) {
    this.apiClient = config.apiClient;
    this.cacheManager = config.cacheManager;
    this.ttl = config.ttl || {
      countries: RecommendedTTL.countries,
      states: RecommendedTTL.states,
      cities: RecommendedTTL.cities
    };
    this.pendingRequests = new Map();
    this.lastSync = null;
  }

  // ============================================================================
  // Countries
  // ============================================================================

  /**
   * Get all countries with caching
   */
  async getCountries(): Promise<Country[]> {
    const cacheKey = CacheKeys.countries();

    // Check cache first
    const cached = this.cacheManager.get<Country[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const countries = await this.apiClient.fetchCountries();
        this.cacheManager.set(cacheKey, countries, this.ttl.countries);
        this.lastSync = new Date();
        return countries;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<Country[]>(cacheKey);
        if (expiredCache) {
          console.warn('Using expired cache for countries due to API error:', error);
          return expiredCache;
        }
        throw this.wrapError(error, 'Failed to load countries');
      }
    });
  }

  /**
   * Get country details by ISO2 code with caching
   */
  async getCountryDetails(iso2: string): Promise<CountryDetails> {
    const cacheKey = CacheKeys.countryDetails(iso2);

    // Check cache first
    const cached = this.cacheManager.get<CountryDetails>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const details = await this.apiClient.fetchCountryDetails(iso2);
        this.cacheManager.set(cacheKey, details, this.ttl.countries);
        this.lastSync = new Date();
        return details;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<CountryDetails>(cacheKey);
        if (expiredCache) {
          console.warn(`Using expired cache for country ${iso2} due to API error:`, error);
          return expiredCache;
        }
        throw this.wrapError(error, `Failed to load details for country ${iso2}`);
      }
    });
  }

  // ============================================================================
  // States
  // ============================================================================

  /**
   * Get states by country ISO2 code with caching
   */
  async getStates(countryIso2: string): Promise<State[]> {
    const cacheKey = CacheKeys.states(countryIso2);

    // Check cache first
    const cached = this.cacheManager.get<State[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const states = await this.apiClient.fetchStates(countryIso2);
        this.cacheManager.set(cacheKey, states, this.ttl.states);
        this.lastSync = new Date();
        return states;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<State[]>(cacheKey);
        if (expiredCache) {
          console.warn(`Using expired cache for states of ${countryIso2} due to API error:`, error);
          return expiredCache;
        }
        throw this.wrapError(error, `Failed to load states for country ${countryIso2}`);
      }
    });
  }

  /**
   * Get all states globally with caching
   */
  async getAllStates(query?: string): Promise<StateWithCountry[]> {
    // For search queries, skip cache and fetch directly
    if (query) {
      try {
        return await this.apiClient.fetchAllStates(query);
      } catch (error) {
        throw this.wrapError(error, 'Failed to search states');
      }
    }

    const cacheKey = 'states:all';

    // Check cache first
    const cached = this.cacheManager.get<StateWithCountry[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const states = await this.apiClient.fetchAllStates();
        // Use longer TTL for global states (7 days)
        this.cacheManager.set(cacheKey, states, 7 * 24 * 60 * 60 * 1000);
        this.lastSync = new Date();
        return states;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<StateWithCountry[]>(cacheKey);
        if (expiredCache) {
          console.warn('Using expired cache for all states due to API error:', error);
          return expiredCache;
        }
        throw this.wrapError(error, 'Failed to load all states');
      }
    });
  }

  /**
   * Get state details with caching
   */
  async getStateDetails(countryIso2: string, stateIso2: string): Promise<StateDetails> {
    const cacheKey = CacheKeys.stateDetails(countryIso2, stateIso2);

    // Check cache first
    const cached = this.cacheManager.get<StateDetails>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const details = await this.apiClient.fetchStateDetails(countryIso2, stateIso2);
        this.cacheManager.set(cacheKey, details, this.ttl.states);
        this.lastSync = new Date();
        return details;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<StateDetails>(cacheKey);
        if (expiredCache) {
          console.warn(`Using expired cache for state ${countryIso2}:${stateIso2} due to API error:`, error);
          return expiredCache;
        }
        throw this.wrapError(error, `Failed to load details for state ${stateIso2} in ${countryIso2}`);
      }
    });
  }

  // ============================================================================
  // Cities
  // ============================================================================

  /**
   * Get cities by country and state ISO2 codes with caching
   */
  async getCities(countryIso2: string, stateIso2: string): Promise<City[]> {
    const cacheKey = CacheKeys.cities(countryIso2, stateIso2);

    // Check cache first
    const cached = this.cacheManager.get<City[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const cities = await this.apiClient.fetchCities(countryIso2, stateIso2);
        this.cacheManager.set(cacheKey, cities, this.ttl.cities);
        this.lastSync = new Date();
        return cities;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<City[]>(cacheKey);
        if (expiredCache) {
          console.warn(`Using expired cache for cities of ${countryIso2}:${stateIso2} due to API error:`, error);
          return expiredCache;
        }
        throw this.wrapError(error, `Failed to load cities for state ${stateIso2} in ${countryIso2}`);
      }
    });
  }

  /**
   * Get cities by country ISO2 code (without state filter) with caching
   * WARNING: For large countries, this can return thousands of cities
   */
  async getCitiesByCountry(countryIso2: string): Promise<CityBasic[]> {
    const cacheKey = CacheKeys.citiesByCountry(countryIso2);

    // Check cache first
    const cached = this.cacheManager.get<CityBasic[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const cities = await this.apiClient.fetchCitiesByCountry(countryIso2);
        this.cacheManager.set(cacheKey, cities, this.ttl.cities);
        this.lastSync = new Date();
        return cities;
      } catch (error) {
        // Fallback to expired cache if available
        const expiredCache = this.getExpiredCache<CityBasic[]>(cacheKey);
        if (expiredCache) {
          console.warn(`Using expired cache for cities of ${countryIso2} due to API error:`, error);
          return expiredCache;
        }
        throw this.wrapError(error, `Failed to load cities for country ${countryIso2}`);
      }
    });
  }

  // ============================================================================
  // Validation
  // ============================================================================

  /**
   * Validate location selection hierarchy
   * Ensures that state belongs to country and city belongs to state
   */
  async validateLocation(location: LocationSelection): Promise<ValidationResult> {
    const errors: ValidationError[] = [];

    // Validate country
    if (!location.country) {
      errors.push({
        field: 'country',
        message: 'Country is required',
        code: 'COUNTRY_REQUIRED'
      });
      return { valid: false, errors };
    }

    // Validate state if provided
    if (location.state) {
      try {
        const states = await this.getStates(location.country.iso2);
        const stateExists = states.some(s => s.iso2 === location.state?.iso2);
        
        if (!stateExists) {
          errors.push({
            field: 'state',
            message: `State ${location.state.name} does not belong to country ${location.country.name}`,
            code: 'INVALID_STATE_COUNTRY'
          });
        }
      } catch (error: unknown) {
        console.error('State validation error:', error);
        errors.push({
          field: 'state',
          message: 'Failed to validate state',
          code: 'VALIDATION_ERROR'
        });
      }
    }

    // Validate city if provided
    if (location.city && location.state) {
      try {
        const cities = await this.getCities(location.country.iso2, location.state.iso2);
        const cityExists = cities.some(c => c.id === location.city?.id);
        
        if (!cityExists) {
          errors.push({
            field: 'city',
            message: `City ${location.city.name} does not belong to state ${location.state.name}`,
            code: 'INVALID_CITY_STATE'
          });
        }
      } catch (error: unknown) {
        console.error('City validation error:', error);
        errors.push({
          field: 'city',
          message: 'Failed to validate city',
          code: 'VALIDATION_ERROR'
        });
      }
    } else if (location.city && !location.state) {
      errors.push({
        field: 'city',
        message: 'State is required when city is selected',
        code: 'STATE_REQUIRED_FOR_CITY'
      });
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // ============================================================================
  // Cache Management
  // ============================================================================

  /**
   * Invalidate cache by type
   */
  invalidateCache(type?: 'countries' | 'states' | 'cities'): void {
    if (!type) {
      // Clear all cache
      this.cacheManager.clear();
      return;
    }

    // Clear specific type
    switch (type) {
      case 'countries':
        this.cacheManager.invalidatePattern(/^countr(y|ies):/);
        break;
      case 'states':
        this.cacheManager.invalidatePattern(/^state(s)?:/);
        break;
      case 'cities':
        this.cacheManager.invalidatePattern(/^cities:/);
        break;
    }
  }

  /**
   * Get last successful sync timestamp
   */
  getLastSync(): Date | null {
    return this.lastSync;
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cacheManager.getStats();
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Deduplicate concurrent requests for the same resource
   */
  private async deduplicateRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    // Create new request
    const promise = requestFn().finally(() => {
      // Remove from pending requests when done
      this.pendingRequests.delete(key);
    });

    // Store pending request
    this.pendingRequests.set(key, promise);

    return promise;
  }

  /**
   * Get expired cache entry (for fallback)
   * This bypasses the TTL check to retrieve stale data
   */
  private getExpiredCache<T>(key: string): T | null {
    return this.cacheManager.peek<T>(key);
  }

  /**
   * Wrap errors with user-friendly messages
   */
  private wrapError(error: unknown, message: string): Error {
    if (error instanceof LocationValidationError) {
      return error;
    }

    // Create user-friendly error
    const errorMessage = error instanceof Error ? error.message : 'Please try again later.';
    const wrappedError = new Error(`${message}. ${errorMessage}`);
    // Store original error for debugging
    Object.assign(wrappedError, { originalError: error });
    return wrappedError;
  }
}

/**
 * Create a singleton instance of LocationService
 */
let serviceInstance: LocationService | null = null;

export function getLocationService(config?: LocationServiceConfig): LocationService {
  if (!serviceInstance && config) {
    serviceInstance = new LocationService(config);
  }
  if (!serviceInstance) {
    throw new Error('LocationService not initialized. Call getLocationService with config first.');
  }
  return serviceInstance;
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetLocationService(): void {
  serviceInstance = null;
}
